import { create } from 'zustand';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';

// Reading preferences for the inbox chat modal (the design's "Aa" popover).
// Width + font size only for now; the Design layouts (Bubbles/Transcript/
// Terminal) are a deferred follow-up. Persisted to localStorage so the choice
// carries across every asset the user opens.

export type ReadingWidth = 'cozy' | 'wide' | 'full';
export type ReadingFontSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface ReadingPrefs {
  width: ReadingWidth;
  fontSize: ReadingFontSize;
  /**
   * How many windows may sit side by side (1–5). The window layer shows fewer
   * while the area is too narrow for that many (window-rules.ts).
   */
  sideBySide: number;
}

/** The choices for `sideBySide`. */
export const SIDE_BY_SIDE_CHOICES = [1, 2, 3, 4, 5] as const;

const LS_KEY = 'eddy.chat.reading-prefs';
/**
 * Bumped when the default width changes: a stored record from an older version
 * keeps its font size but takes the new default width. Version 3 writes
 * `width` only once the person has picked one; a version-2 record's Cozy or
 * Wide is kept as a choice, its Full (indistinguishable from the default) is
 * not. The default is Full.
 */
const PREFS_VERSION = 3;
const DEFAULTS: ReadingPrefs = { width: 'full', fontSize: 'medium', sideBySide: 1 };

/** Modal card max-width per width pref (px). */
export const READING_CARD_PX: Record<ReadingWidth, number> = { cozy: 880, wide: 1120, full: 1440 };
/** Centred reading-column max-width per width pref (px). */
export const READING_COLUMN_PX: Record<ReadingWidth, number> = { cozy: 700, wide: 820, full: 960 };
/** Base reading font size per font pref (px). */
export const READING_FONT_PX: Record<ReadingFontSize, number> = { small: 15, medium: 17, large: 19, xlarge: 22 };

/**
 * Pure parse of the stored record (`raw` = the localStorage string, or null),
 * plus whether its width is the person's own pick. Absent or malformed →
 * defaults, nothing picked.
 */
export function parseStoredReadingPrefs(raw: string | null): { prefs: ReadingPrefs; widthChosen: boolean } {
  if (!raw) return { prefs: DEFAULTS, widthChosen: false };
  try {
    const p = JSON.parse(raw) as (Partial<ReadingPrefs> & { v?: number }) | null;
    if (!p || typeof p !== 'object') return { prefs: DEFAULTS, widthChosen: false };
    const validWidth = typeof p.width === 'string' && Object.hasOwn(READING_CARD_PX, p.width);
    const widthChosen = validWidth && (p.v === PREFS_VERSION || (p.v === 2 && p.width !== 'full'));
    return {
      prefs: {
        width: widthChosen ? (p.width as ReadingWidth) : DEFAULTS.width,
        fontSize: typeof p.fontSize === 'string' && Object.hasOwn(READING_FONT_PX, p.fontSize) ? p.fontSize : DEFAULTS.fontSize,
        sideBySide: (SIDE_BY_SIDE_CHOICES as readonly unknown[]).includes(p.sideBySide) ? (p.sideBySide as number) : DEFAULTS.sideBySide,
      },
      widthChosen,
    };
  } catch {
    return { prefs: DEFAULTS, widthChosen: false };
  }
}

/** The stored record's prefs, `width` falling back to the default (`full`) when none was picked. */
export function parseReadingPrefs(raw: string | null): ReadingPrefs {
  return parseStoredReadingPrefs(raw).prefs;
}

/** The stored record for `prefs`, always carrying the current version so a choice made now survives the next read. */
export function serializeReadingPrefs(prefs: ReadingPrefs, widthChosen = true): string {
  return JSON.stringify({ v: PREFS_VERSION, ...(widthChosen ? { width: prefs.width } : {}), fontSize: prefs.fontSize, sideBySide: prefs.sideBySide });
}

function loadReadingPrefs(): { prefs: ReadingPrefs; widthChosen: boolean } {
  try {
    return parseStoredReadingPrefs(localStorage.getItem(LS_KEY));
  } catch {
    return { prefs: DEFAULTS, widthChosen: false };
  }
}

/**
 * One shared copy of the reading prefs for the whole app. They are set in
 * User settings and the sidebar's user menu now (FRONT-3148, eddy 2026-10-26)
 * and read by every open chat / Build window, so a change must reach windows
 * that are already open — a per-component `useState` only re-read storage on
 * mount. Another browser tab's change arrives through the `storage` event.
 */
export const useReadingPrefsStore = create<{ prefs: ReadingPrefs; widthChosen: boolean }>(() => loadReadingPrefs());

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === LS_KEY) useReadingPrefsStore.setState(parseStoredReadingPrefs(e.newValue));
  });
}

export function updateReadingPrefs(patch: Partial<ReadingPrefs>): void {
  const state = useReadingPrefsStore.getState();
  const next = { ...state.prefs, ...patch };
  const widthChosen = state.widthChosen || patch.width !== undefined;
  try {
    localStorage.setItem(LS_KEY, serializeReadingPrefs(next, widthChosen));
  } catch {
    /* ignore — the in-memory value still applies for this session */
  }
  useReadingPrefsStore.setState({ prefs: next, widthChosen });
}

export function useReadingPrefs(): [ReadingPrefs, (patch: Partial<ReadingPrefs>) => void] {
  // Selects the stored values themselves (stable until they change), never a
  // derived object — see window-selectors.test.ts for why that matters; the
  // resolved copy below is memoized on them.
  const prefs = useReadingPrefsStore((s) => s.prefs);
  return [prefs, updateReadingPrefs];
}

interface SegOption {
  value: string;
  label: ReactNode;
}

function Segmented({
  value,
  options,
  onChange,
}: {
  value: string;
  options: SegOption[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex gap-1 rounded-[10px] bg-muted p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'flex-1 rounded-md px-2 py-1.5 text-[length:var(--text-sm)] inline-flex items-center justify-center transition-colors',
            value === o.value
              ? 'bg-card text-foreground border border-border shadow-sm font-medium'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PrefGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <div className="mb-2 uppercase text-[length:var(--text-xs)] font-semibold tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
      {children}
    </div>
  );
}

/**
 * The Width + Font size controls, with no popover/dialog chrome of its own —
 * a plain panel body. Extracted (FRONT-1668) so the two hosts that need
 * these controls — the inbox chat modal's "Aa" popover (`ReadingPrefsButton`
 * below, unchanged) and the coding session window's "Appearance" dialog —
 * share one definition instead of drifting apart.
 */
export function ReadingPrefsPanel({
  prefs,
  onChange,
}: {
  prefs: ReadingPrefs;
  onChange: (patch: Partial<ReadingPrefs>) => void;
}) {
  return (
    <>
      <PrefGroup label="Width">
        <Segmented
          value={prefs.width}
          onChange={(v) => onChange({ width: v as ReadingWidth })}
          options={[
            { value: 'cozy', label: 'Cozy' },
            { value: 'wide', label: 'Wide' },
            { value: 'full', label: 'Full' },
          ]}
        />
      </PrefGroup>
      <PrefGroup label="Font size">
        <Segmented
          value={prefs.fontSize}
          onChange={(v) => onChange({ fontSize: v as ReadingFontSize })}
          options={[
            { value: 'small', label: <span style={{ fontSize: 13 }}>A</span> },
            { value: 'medium', label: <span style={{ fontSize: 15 }}>A</span> },
            { value: 'large', label: <span style={{ fontSize: 18 }}>A</span> },
            { value: 'xlarge', label: <span style={{ fontSize: 22 }}>A</span> },
          ]}
        />
      </PrefGroup>
    </>
  );
}

/**
 * The "Aa" reading-preferences button + popover. Closes on outside click.
 */
export function ReadingPrefsButton({
  prefs,
  onChange,
}: {
  prefs: ReadingPrefs;
  onChange: (patch: Partial<ReadingPrefs>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Tip label="Reading preferences">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Reading preferences"
          className={cn(
            'inline-flex items-center gap-1 h-8 px-2.5 rounded-[10px] border text-[length:var(--text-sm)] transition-colors',
            open
              ? 'border-primary text-foreground ring-2 ring-primary/15'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          <span className="font-medium">Aa</span>
          <CaretDown size={11} weight="bold" className={cn('transition-transform', open && 'rotate-180')} />
        </button>
      </Tip>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-[60] w-[304px] rounded-[14px] border border-border bg-card p-4 shadow-[var(--shadow-lg)]">
          <ReadingPrefsPanel prefs={prefs} onChange={onChange} />
        </div>
      )}
    </div>
  );
}
